import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store';
import { patchScorecard, selectListData } from 'store/scorecards';
import {
  formatScorecardData,
  parseScorecardData,
  getScorecardStates,
  isOwnerScorecardEditingStatus,
} from 'helpers';
import { FormValues } from 'components/ScorecardDetails/interfaces';
import { useScorecardContext } from 'contexts';
import { useToast, usePagePermissions, useScorecardData } from 'hooks';

export type InlineField =
  | 'goalName'
  | 'goalDescription'
  | 'goalWeight'
  | 'subgoalName'
  | 'subgoalDescription'
  | 'subgoalWeight';

interface SaveFieldArgs {
  goalId: number;
  subgoalId?: number;
  field: InlineField;
  value: string | number;
}

const messages = defineMessages({
  goalName: {
    id: 'pages.scorecards.view.goalNameUpdated',
    defaultMessage: 'Goal name updated',
  },
  goalDescription: {
    id: 'pages.scorecards.view.goalDescriptionUpdated',
    defaultMessage: 'Goal description updated',
  },
  goalWeight: {
    id: 'pages.scorecards.view.goalWeightUpdated',
    defaultMessage: 'Goal weight updated',
  },
  subgoalName: {
    id: 'pages.scorecards.view.subgoalNameUpdated',
    defaultMessage: 'Subgoal name updated',
  },
  subgoalDescription: {
    id: 'pages.scorecards.view.subgoalDescriptionUpdated',
    defaultMessage: 'Subgoal description updated',
  },
  subgoalWeight: {
    id: 'pages.scorecards.view.subgoalWeightUpdated',
    defaultMessage: 'Subgoal weight updated',
  },
});

export default function useScorecardInlineSave() {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const showMessage = useToast();
  const scorecard = useScorecardData();
  const { id, store, enqueueScorecardMutation } = useScorecardContext();
  const { isOwnScorecardsPage } = usePagePermissions();

  const { version: listVersion } = useSelector(selectListData(store, id)) || {};
  const { version: userScorecardVersion } =
    useSelector(selectListData('userScorecards', id)) || {};
  const version = (scorecard.version ||
    listVersion ||
    userScorecardVersion) as number;
  const scorecardSnapshot = useMemo(
    () => ({ ...scorecard, version }),
    [scorecard, version],
  );

  const saveField = useCallback(
    async ({ goalId, subgoalId, field, value }: SaveFieldArgs) => {
      await enqueueScorecardMutation(scorecardSnapshot, async (latest) => {
        const { isFeedbackCollected, isCompleted } = getScorecardStates(
          latest.status,
        );
        const withRestrictions =
          isOwnScorecardsPage || isFeedbackCollected || isCompleted;
        const isActiveOwnScorecard =
          isOwnScorecardsPage && isOwnerScorecardEditingStatus(latest.status);

        const goalIndex = latest.goals.findIndex((g) => g.id === goalId);
        if (goalIndex < 0) return undefined;

        const goals = parseScorecardData(latest.goals);
        const goal = goals[goalIndex];
        if (!goal) return undefined;

        let target: { name: string; description?: string; weight: unknown } =
          goal;
        if (typeof subgoalId === 'number') {
          const subgoalIndex = latest.goals[goalIndex].subgoals.findIndex(
            (subgoal) => subgoal.id === subgoalId,
          );
          if (subgoalIndex < 0) return undefined;
          target = goal.subgoals[subgoalIndex] as never;
        }

        switch (field) {
          case 'goalName':
          case 'subgoalName':
            target.name = String(value);
            break;
          case 'goalDescription':
          case 'subgoalDescription':
            target.description = String(value);
            break;
          case 'goalWeight':
          case 'subgoalWeight':
            target.weight = value as never;
            break;
          default:
            break;
        }

        const data = formatScorecardData(
          { id, goals, totalWeight: 0, entity: 'scorecard' } as FormValues,
          latest.version,
          withRestrictions,
          isActiveOwnScorecard,
        );

        return dispatch(
          patchScorecard({ id, store, data: { ...data, inlineEdit: true } }),
        ).unwrap();
      });

      showMessage({
        severity: 'success',
        message: formatMessage(messages[field]),
      });
    },
    [
      dispatch,
      enqueueScorecardMutation,
      formatMessage,
      id,
      isOwnScorecardsPage,
      scorecardSnapshot,
      showMessage,
      store,
    ],
  );

  return { saveField };
}
